import React, { useState, useEffect } from 'react';
import { Navbar, NavDropdown, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { motion, AnimatePresence } from 'framer-motion';
import { IoIosSearch } from "react-icons/io";
import { IoCalendar } from "react-icons/io5";
import { BsFullscreen } from "react-icons/bs";
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logout, setCurrentAcademicYear } from '../application/reducers/auth';
import ProtonCalendar from '../pages/calendar/ProtonCalendar';
import ProfileModal from './ProfileModal';
import PreferencesModal from './PreferencesModal';
import ProfileImage from './ProfileImage';
import DynamicBrand from './DynamicBrand';
import NotificationPanel from './NotificationPanel';
import './Navbar.scss';

const NavIconButton = ({ tooltip, children, ...props }) => (
  <OverlayTrigger
    placement="bottom"
    overlay={<Tooltip id={`tooltip-${tooltip}`}>{tooltip}</Tooltip>}
  >
    <motion.button
      type="button"
      className="nav-icon-btn"
      aria-label={tooltip}
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.92 }}
      {...props}
    >
      {children}
    </motion.button>
  </OverlayTrigger>
);

const CustomNavbar = ({ onFullscreen }) => {
  const [profileVisible, setProfileVisible] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const [showCalender, setShowCalender] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showPreferencesModal, setShowPreferencesModal] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { academic_years, current_academic_id, user, role } = useSelector((state) => state.auth);
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.profile-container')) {
        setProfileVisible(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const toggleProfile = () => {
    setProfileVisible(!profileVisible);
  };

  const handleLogout = () => {
    dispatch(logout());
    setProfileVisible(false);
    // Navigate to sign-in page after logout
    navigate('/');
  };


  const searchVariants = {
    normal: {
      scale: 1,
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
      transition: { duration: 0.1 }
    },
    focused: {
      scale: 1.01,
      boxShadow: "0 4px 16px rgba(9, 72, 179, 0.15)",
      transition: { duration: 0.15, ease: "easeOut" }
    }
  };


  const ProfilePopover = () => (
    <motion.div
      className="profile-popover"
      initial={{ opacity: 0, y: -20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -20, scale: 0.95 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
    >
      <motion.div
        className="popover-header"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1, duration: 0.2 }}
      >
        <div className="avatar-container">
          <ProfileImage
            user={user}
            size="medium"
            roundedCircle
            className="avatar-image"
          />
          <div className="status-indicator"></div>
        </div>
        <div className="user-info">
          <h4 className="user-name">{user?.first_name} {user?.last_name}</h4>
          <p className="user-email">{user?.email}</p>
          <span className="role-badge">{role?.role_name}</span>
        </div>
      </motion.div>

      <motion.div
        className="popover-body"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.15, duration: 0.2 }}
      >
        <motion.button
          type="button"
          className="popover-item"
          whileHover={{ x: 4 }}
          whileTap={{ scale: 0.98 }}
          transition={{ duration: 0.15 }}
          onClick={() => {
            setShowProfileModal(true);
            setProfileVisible(false);
          }}
        >
          <span className="item-icon">👤</span>
          <span className="item-text">Profile Settings</span>
        </motion.button>

        <motion.button
          type="button"
          className="popover-item"
          whileHover={{ x: 4 }}
          whileTap={{ scale: 0.98 }}
          transition={{ duration: 0.15 }}
          onClick={() => {
            setShowPreferencesModal(true);
            setProfileVisible(false);
          }}
        >
          <span className="item-icon">⚙️</span>
          <span className="item-text">Preferences</span>
        </motion.button>

        <motion.button
          type="button"
          className="popover-item"
          whileHover={{ x: 4 }}
          whileTap={{ scale: 0.98 }}
          transition={{ duration: 0.15 }}
          onClick={() => {
            setProfileVisible(false);
            navigate('/subscription');
          }}
        >
          <span className="item-icon">💳</span>
          <span className="item-text">Subscription</span>
        </motion.button>

        <motion.button
          type="button"
          className="popover-item"
          whileHover={{ x: 4 }}
          whileTap={{ scale: 0.98 }}
          transition={{ duration: 0.15 }}
          onClick={() => {
            setProfileVisible(false);
          }}
        >
          <span className="item-icon">📊</span>
          <span className="item-text">Analytics</span>
        </motion.button>

        <motion.a
          href="https://proton.education"
          target='_blank'
          className="popover-item"
          whileHover={{ x: 4 }}
          whileTap={{ scale: 0.98 }}
          transition={{ duration: 0.15 }}
        >
          <span className="item-icon">💡</span>
          <span className="item-text">Help & Support</span>
        </motion.a>

        <div className="popover-divider"></div>

        <motion.button
          className="popover-item logout-item"
          whileHover={{ x: 4 }}
          whileTap={{ scale: 0.98 }}
          transition={{ duration: 0.15 }}
          onClick={handleLogout}
        >
          <span className="item-icon">🚪</span>
          <span className="item-text">Sign Out</span>
        </motion.button>
      </motion.div>
    </motion.div>
  );



  return (

    <Navbar bg="transparent" expand="lg" sticky="top" className="enhanced-navbar">
      <DynamicBrand className={'brand-logo'} />

      <div className="nav-right">
        <motion.div
          role="search"
          className="search-container"
          variants={searchVariants}
          animate={searchFocused ? "focused" : "normal"}
        >
          <input
            placeholder="Search anything..."
            type="search"
            aria-label="Search"
            className="form-control search"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setSearchValue('');
                e.currentTarget.blur();
              }
            }}
          />
          <motion.span
            className="search-icon"
            aria-hidden="true"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            <IoIosSearch size={20} />
          </motion.span>
          {searchValue && (
            <motion.button
              type="button"
              aria-label="Clear search"
              className="search-clear"
              onClick={() => setSearchValue('')}
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.5 }}
              transition={{ duration: 0.15, ease: "easeOut" }}
              whileHover={{ scale: 1.1, backgroundColor: "rgba(220, 53, 69, 0.1)" }}
              whileTap={{ scale: 0.9 }}
            >
              ×
            </motion.button>
          )}
        </motion.div>

        <div className="right-actions-container">
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <NavDropdown
              className="academic-selection"
              id="academic-selection"
              title={`Academic ${academic_years?.find((i) => i.academic_id === current_academic_id)?.year_label}`}
            >
              {
                academic_years?.map((year) => (
                  <NavDropdown.Item
                    key={year.academic_id}
                    as="button"
                    onClick={() => {
                      dispatch(setCurrentAcademicYear(year.academic_id));
                    }}
                    active={year.academic_id === current_academic_id}
                  >
                    Academic {year.year_label}
                  </NavDropdown.Item>
                ))
              }

            </NavDropdown>
          </motion.div>

          <span className="nav-divider" />

          <div className="nav-icon-group">
            <NavIconButton
              tooltip="Calendar"
              onClick={() => setShowCalender(!showCalender)}
            >
              <IoCalendar size={18} color="#fff" />
            </NavIconButton>

            <NotificationPanel />

            <NavIconButton
              tooltip="Fullscreen (F11)"
              onClick={onFullscreen}
            >
              <BsFullscreen size={15} color="#fff" />
            </NavIconButton>
          </div>

          <span className="nav-divider" />

          <span className="profile-container">
            <motion.div
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              <ProfileImage
                user={user}
                size="small"
                onClick={toggleProfile}
                className="profile-pic"
                roundedCircle
              />
              <motion.div
                className="online-indicator"
                animate={{
                  scale: [1, 1.1, 1],
                  opacity: [0.8, 1, 0.8]
                }}
                transition={{
                  duration: 2.5,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />
            </motion.div>

            <AnimatePresence>
              {profileVisible && <ProfilePopover />}
            </AnimatePresence>
          </span>
        </div>
      </div>
      {
        showCalender && (
          <motion.div
            className="calendar-popover"
            initial={{ opacity: 0, y: -20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
          >
            <ProtonCalendar onClose={setShowCalender} />
          </motion.div>
        )
      }
      
      {/* Profile Modal */}
      <ProfileModal 
        show={showProfileModal} 
        onHide={() => setShowProfileModal(false)} 
      />

      {/* Preferences Modal */}
      <PreferencesModal 
        show={showPreferencesModal} 
        onHide={() => setShowPreferencesModal(false)} 
      />

    
    </Navbar>
  );
};

export default CustomNavbar;
